import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  searchAdminComments,
  hideAdminComment,
  unhideAdminComment,
  deleteAdminComment,
  hideAdminCommentsBulk,
  unhideAdminCommentsBulk,
  deleteAdminCommentsBulk,
  type AdminCommentItem,
} from "../api/adminCommentsApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import { useAdminSession } from "../context/AdminSessionContext";
import { hasPermission } from "../utils/permissionHelpers";

const PAGE_SIZE = 20;

type CommentFilters = {
  username: string;
  contentId: string;
  contentPartId: string;
  fromUtc: string;
  toUtc: string;
  visibility: "all" | "visible" | "hidden";
};

const INITIAL_FILTERS: CommentFilters = {
  username: "",
  contentId: "",
  contentPartId: "",
  fromUtc: "",
  toUtc: "",
  visibility: "all",
};

export default function AdminComments() {
  const { loading: checkingPermissions, allowed } = usePermissionCheck("Comments.View");
  const { session } = useAdminSession();
  const canModerate = useMemo(() => session ? hasPermission(session.permissions, "Comments.Moderate") : false, [session]);
  const canDelete = useMemo(() => session ? hasPermission(session.permissions, "Comments.Delete") : false, [session]);
  const [filters, setFilters] = useState<CommentFilters>({ ...INITIAL_FILTERS });
  const [items, setItems] = useState<AdminCommentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const performSearch = async (reset = false) => {
    setIsLoading(true);
    const response = await searchAdminComments({
      username: filters.username.trim() || undefined,
      contentId: filters.contentId.trim() || undefined,
      contentPartId: filters.contentPartId.trim() || undefined,
      fromUtc:filters.fromUtc ? new Date(filters.fromUtc).toISOString() : undefined,
      toUtc:filters.toUtc ? new Date(filters.toUtc).toISOString() : undefined,
      isHidden: filters.visibility === "all" ? undefined : filters.visibility === "hidden",
      skip: reset ? 0 : skip,
      take: PAGE_SIZE,
    });

    setTotal(response.totalCount);
    setItems((prev: AdminCommentItem[]) => (reset ? response.items : [...prev, ...response.items]));
    setSkip((prev: number) => (reset ? response.items.length : prev + response.items.length));
    setSelectedIds((prev: string[]) => {
      if (reset) {
        return [];
      }
      const currentIds = new Set(response.items.map((item) => item.id));
      return prev.filter((id) => currentIds.has(id));
    });
    setIsLoading(false);
  };

  useEffect(() => {
    if (allowed) {
      void performSearch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const resetAndSearch = () => {
    setSkip(0);
    void performSearch(true);
  };

  const handleCommentAction = async (commentId: string, action: "hide" | "unhide" | "delete") => {
    if ((action === "hide" || action === "unhide") && !canModerate) {
      Swal.fire("Yetki yok", "Bu işlem için Comments.Moderate izni gerekiyor.", "warning");
      return;
    }

    if (action === "delete" && !canDelete) {
      Swal.fire("Yetki yok", "Yalnızca Admin veya Süper Moderatör yorum silebilir.", "warning");
      return;
    }

    let success = false;
    if (action === "hide") {
      success = await hideAdminComment(commentId);
    } else if (action === "unhide") {
      success = await unhideAdminComment(commentId);
    } else {
      const confirm = await Swal.fire({
        title: "Delete this comment?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc2626",
      });

      if (!confirm.isConfirmed) {
        return;
      }

      success = await deleteAdminComment(commentId);
    }

    if (!success) {
      Swal.fire("Error", "Comment action failed.", "error");
      return;
    }

    Swal.fire("Updated", "Comment status changed.", "success");
    setItems((prev: AdminCommentItem[]) => prev.filter((item) => item.id !== commentId));
    setTotal((prev: number) => Math.max(0, prev - 1));
  };

  const toggleSelection = (commentId: string) => {
    setSelectedIds((prev: string[]) =>
      prev.includes(commentId) ? prev.filter((id) => id !== commentId) : [...prev, commentId],
    );
  };

  const toggleSelectAllOnPage = (checked: boolean) => {
    if (checked) {
      const ids = items.map((item) => item.id);
      setSelectedIds((prev: string[]) => Array.from(new Set([...prev, ...ids])));
    } else {
      const ids = new Set(items.map((item) => item.id));
      setSelectedIds((prev: string[]) => prev.filter((id) => !ids.has(id)));
    }
  };

  const handleBulkAction = async (type: "hide" | "unhide" | "delete") => {
    if (selectedIds.length === 0) {
      return;
    }

    if (type === "hide" || type === "unhide") {
      if (!canModerate) {
        Swal.fire("Yetki yok", "Bu işlem için Comments.Moderate izni gerekiyor.", "warning");
        return;
      }
    }

    if (type === "delete" && !canDelete) {
      Swal.fire("Yetki yok", "Yalnızca Admin veya Süper Moderatör yorum silebilir.", "warning");
      return;
    }

    let reason = "";
    if (type !== "unhide") {
      const prompt = await Swal.fire({
        title: type === "delete" ? "Silme gerekçesi" : "Gizleme gerekçesi",
        input: "text",
        inputPlaceholder: "Opsiyonel",
        showCancelButton: true,
        confirmButtonText: "Devam",
      });
      if (!prompt.isConfirmed) {
        return;
      }
      reason = prompt.value ?? "";
    }

    let success = false;
    if (type === "hide") {
      success = await hideAdminCommentsBulk(selectedIds, reason);
    } else if (type === "unhide") {
      success = await unhideAdminCommentsBulk(selectedIds);
    } else {
      success = await deleteAdminCommentsBulk(selectedIds, reason);
    }

    if (!success) {
      Swal.fire("Hata", "Toplu işlem başarısız oldu.", "error");
      return;
    }

    Swal.fire("Başarılı", "İşlem uygulandı.", "success");
    setItems((prev: AdminCommentItem[]) => prev.filter((item) => !selectedIds.includes(item.id)));
    setSelectedIds([]);
    setTotal((prev: number) => Math.max(0, prev - selectedIds.length));
  };

  if (checkingPermissions) {
    return <div className="p-8 text-center text-sm text-gray-500">Yetkiler kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return <div className="p-8 text-center text-sm text-red-500">Bu sayfaya erişim izniniz yok.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-gray-800">Yorum Moderasyonu</h1>
        <p className="text-sm text-gray-500">İçerik ve bölüm yorumlarını filtrele, gizle/göster veya sil.</p>
      </div>

      <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 text-sm shadow-sm">
        <div className="grid gap-3 md:grid-cols-4">
          <label className="text-xs uppercase tracking-wide text-gray-500">
            <div className="mb-1">Kullanıcı</div>
            <input
              className="w-full rounded border border-gray-300 p-2 text-sm"
              placeholder="kullanıcı adı"
              value={filters.username}
              onChange={(e) => setFilters((prev) => ({ ...prev, username: e.target.value }))}
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-gray-500">
            <div className="mb-1">İçerik ID</div>
            <input
              className="w-full rounded border border-gray-300 p-2 text-sm"
              placeholder="content guid"
              value={filters.contentId}
              onChange={(e) => setFilters((prev) => ({ ...prev, contentId: e.target.value }))}
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-gray-500">
            <div className="mb-1">Bölüm ID</div>
            <input
              className="w-full rounded border border-gray-300 p-2 text-sm"
              placeholder="part guid"
              value={filters.contentPartId}
              onChange={(e) => setFilters((prev) => ({ ...prev, contentPartId: e.target.value }))}
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-gray-500">
            <div className="mb-1">Durum</div>
            <select
              className="w-full rounded border border-gray-300 p-2 text-sm"
              value={filters.visibility}
              onChange={(e) => setFilters((prev) => ({ ...prev, visibility: e.target.value as CommentFilters["visibility"] }))}
            >
              <option value="all">Tümü</option>
              <option value="visible">Görünen</option>
              <option value="hidden">Gizli</option>
            </select>
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-xs uppercase tracking-wide text-gray-500">
            <div className="mb-1">Başlangıç (UTC)</div>
            <input
              type="datetime-local"
              className="w-full rounded border border-gray-300 p-2 text-sm"
              value={filters.fromUtc}
              onChange={(e) => setFilters((prev) => ({ ...prev, fromUtc: e.target.value }))}
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-gray-500">
            <div className="mb-1">Bitiş (UTC)</div>
            <input
              type="datetime-local"
              className="w-full rounded border border-gray-300 p-2 text-sm"
              value={filters.toUtc}
              onChange={(e) => setFilters((prev) => ({ ...prev, toUtc: e.target.value }))}
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              onClick={resetAndSearch}
              disabled={isLoading}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? "Yükleniyor..." : "Filtrele"}
            </button>
            <button
              onClick={() => {
                setFilters({ ...INITIAL_FILTERS });
                setSkip(0);
                setItems([]);
                setSelectedIds([]);
                setTotal(0);
              }}
              className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Temizle
            </button>
          </div>
        </div>
      </section>

      <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-slate-500">
            Toplam {total} yorum · Gösterilen {items.length} {selectedIds.length > 0 && `· Seçili ${selectedIds.length}`}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {selectedIds.length > 0 && (
              <>
                {canModerate && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleBulkAction("hide")}
                      className="rounded bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600"
                    >
                      Seçiliyi gizle
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkAction("unhide")}
                      className="rounded bg-slate-600 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700"
                    >
                      Seçiliyi göster
                    </button>
                  </>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => handleBulkAction("delete")}
                    className="rounded bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700"
                  >
                    Seçiliyi sil
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedIds([])}
                  className="rounded border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Seçimi temizle
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => toggleSelectAllOnPage(true)}
              className="rounded border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              disabled={items.length === 0}
            >
              Bu sayfayı seç
            </button>
            <button
              type="button"
              onClick={() => toggleSelectAllOnPage(false)}
              className="rounded border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              disabled={items.length === 0}
            >
              Bu sayfayı temizle
            </button>
          </div>
        </div>
      </div>

      <section className="rounded bg-white p-4 shadow">
        {items.length === 0 && !isLoading && <div className="text-sm text-gray-500">Filtreye uyan yorum bulunamadı.</div>}
        {items.length > 0 && (
          <div className="space-y-3">
            {items.map((comment) => (
              <div key={comment.id} className="rounded border border-gray-200 p-3 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300"
                      checked={selectedIds.includes(comment.id)}
                      onChange={() => toggleSelection(comment.id)}
                    />
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-gray-800">{comment.username}</div>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                            comment.isHidden ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          {comment.isHidden ? "Gizli" : "Görünür"}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {comment.contentTitle || "İçerik"} {comment.contentPartTitle ? `· ${comment.contentPartTitle}` : ""}
                      </div>
                      <div className="text-[11px] font-mono text-gray-500">
                        {comment.contentId && <span>Content: {comment.contentId}</span>}
                        {comment.contentPartId && <span> · Part: {comment.contentPartId}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canModerate && (
                      <button
                        onClick={() => handleCommentAction(comment.id, comment.isHidden ? "unhide" : "hide")}
                        className="rounded bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600"
                      >
                        {comment.isHidden ? "Göster" : "Gizle"}
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleCommentAction(comment.id, "delete")}
                        className="rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
                      >
                        Sil
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-2 whitespace-pre-line text-sm text-gray-800">{comment.text}</div>
                <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-gray-500">
                  <span>{new Date(comment.createdAt).toLocaleString()}</span>
                  {comment.userId && <span>UID: {comment.userId}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
        {items.length < total && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => performSearch(false)}
              disabled={isLoading}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? "Loading..." : "Load more"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
